import type { NormalizedUnreadMessage, UnreadMessageThreadContext } from '../types/unreadMessages'
import { formatMessageTimestamp } from './formatMessageTimestamp'
import { pickFirstString, safeString } from './safeGet'

/**
 * Example payload shapes (MediKaLLink may differ):
 * `{ "data": [ { "sender_name": "…", "subject": "…", "is_urgent": true } ] }`
 * `[ { "from": "…", "title": "…" } ]`
 */
export const MOCK_UNREAD_MESSAGES_RESPONSE = {
  data: [
    {
      id: 'mock-1',
      sender_name: 'Dr. Example',
      recipient_name: 'Dr. Dupont',
      subject: 'Follow-up imaging',
      body_preview: 'Please review the attached report when you have a moment.',
      created_at: '2026-04-20T14:30:00Z',
      is_urgent: true,
      unread: true,
      discussion_id: 'disc-1001',
    },
    {
      id: 'mock-2',
      sender: 'Secrétariat',
      recipient: 'Mme Martin',
      title: 'Appointment change',
      message: 'Patient asked to move Tuesday slot.',
      date: '2026-04-19',
      urgent: false,
      read: false,
    },
  ],
} as const

function asRecord(v: unknown): Record<string, unknown> | null {
  if (v && typeof v === 'object' && !Array.isArray(v)) return v as Record<string, unknown>
  return null
}

function discussionSubject(row: Record<string, unknown>): string {
  const direct = pickFirstString(row, ['discussion_subject', 'discussionSubject'])
  if (direct) return direct
  const discussion = asRecord(row.discussion)
  if (!discussion) return ''
  return pickFirstString(discussion, ['discussion_subject', 'discussionSubject', 'subject']) || ''
}

function isUrgentRow(row: Record<string, unknown>): boolean {
  const flags = ['is_urgent', 'isUrgent', 'urgent', 'priority', 'is_priority', 'urgence']
  for (const k of flags) {
    const v = row[k]
    if (v === true || v === 1 || v === '1') return true
    if (typeof v === 'string' && /^(yes|true|urgent|oui)$/i.test(v.trim())) return true
  }
  return false
}

function isUnreadRow(row: Record<string, unknown>): boolean {
  for (const k of ['unread', 'is_unread', 'isUnread', 'read']) {
    const v = row[k]
    if (k === 'read') {
      if (v === false || v === 0) return true
      continue
    }
    if (v === true || v === 1) return true
  }
  return true
}

/** Thread open ids for in-chat unread/urgent list only (not shared with other mappers). */
function unreadListThreadContext(row: Record<string, unknown>): UnreadMessageThreadContext {
  return {
    discussionId: pickFirstString(row, ['id']),
    conversationId: pickFirstString(row, ['conversation_id', 'conversationId', 'conv_id']),
    threadId: pickFirstString(row, ['thread_id', 'threadId', 'message_thread_id']),
  }
}

function extractRows(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload
  const root = asRecord(payload)
  if (!root) return []
  for (const key of ['data', 'messages', 'items', 'results', 'rows', 'list']) {
    const v = root[key]
    if (Array.isArray(v)) return v
  }
  const nested = asRecord(root.data)
  if (nested) {
    for (const key of ['messages', 'items', 'list']) {
      const v = nested[key]
      if (Array.isArray(v)) return v
    }
  }
  return []
}

/**
 * Maps arbitrary API JSON into card rows; never throws.
 */
export function mapUnreadMessagesResponse(payload: unknown, locale?: string): NormalizedUnreadMessage[] {
  const rows = extractRows(payload)
  const out: NormalizedUnreadMessage[] = []
  let i = 0
  for (const raw of rows) {
    const row = asRecord(raw)
    if (!row) continue
    const id = pickFirstString(row, ['id', 'message_id', 'messageId', 'uuid', 'uid']) || `row-${i}`
    const sender = pickFirstString(row, [
      'sender_name',
      'senderName',
      'sender',
      'from',
      'from_name',
      'author',
      'user_name',
      'display_name',
    ])
    const recipient = pickFirstString(row, [
      'recipient_name',
      'recipientName',
      'recipient',
      'to_name',
      'toName',
      'to_display_name',
      'toDisplayName',
      'addressee',
      'destinataire',
      'whom_name',
      'patient_name',
      'patientName',
      'to',
    ])
    const subject =
      discussionSubject(row) || pickFirstString(row, ['subject', 'title', 'object', 'topic', 'header'])
    const snippet = pickFirstString(row, ['preview', 'body_preview', 'snippet', 'last_message', 'excerpt'])
    const lastMessageText = pickFirstString(row, ['last_message', 'lastMessage']) || ''
    const bodyLong = pickFirstString(row, [
      'message_body',
      'messageBody',
      'full_message',
      'fullMessage',
      'body',
      'message',
      'text',
      'content',
      'html_body',
    ])
    const content = (bodyLong || snippet || '').trim()
    const preview =
      (snippet || '').trim() ||
      (content.length > 200 ? `${content.slice(0, 200).trim()}…` : content)
    const dateRaw =
      pickFirstString(row, ['updated_at', 'updatedAt', 'last_updated_at', 'lastUpdatedAt']) ??
      pickFirstString(row, ['created_at', 'createdAt', 'date_created', 'dateCreated']) ??
      pickFirstString(row, ['date', 'sent_at', 'sentAt', 'timestamp', 'time', 'datetime'])
    const dateLabel = formatMessageTimestamp(dateRaw, locale)
    const urgent = isUrgentRow(row)
    const unread = isUnreadRow(row)
    out.push({
      id,
      sender: sender || '—',
      recipient: recipient || '',
      subject: subject || '(No subject)',
      preview: preview || '',
      lastMessageText: lastMessageText.trim() || preview || content || '',
      content: content || preview || '',
      dateLabel,
      isUrgent: urgent,
      isUnread: unread,
      threadContext: unreadListThreadContext(row),
    })
    i += 1
  }
  return out
}

export function filterByVariant(
  rows: NormalizedUnreadMessage[],
  variant: 'unread' | 'urgent',
  /** When the GET already used ``?urgent=1``, trust the API list and skip client-side urgent flag filtering. */
  apiUrgentParam?: 0 | 1,
): NormalizedUnreadMessage[] {
  if (variant === 'urgent' && apiUrgentParam === 1) {
    return rows
  }
  if (variant === 'urgent') return rows.filter((r) => r.isUrgent)
  return rows
}

/** @internal */
export function _debugFirstKeys(payload: unknown): string {
  const r = asRecord(payload)
  if (!r) return safeString(payload, 80)
  return Object.keys(r).slice(0, 12).join(', ')
}
